import React from 'react';
import { Box, Text, Link } from '@primer/react';
import { GitCommitIcon, RocketIcon, SparkleFillIcon, ToolsIcon, BookIcon, ZapIcon } from '@primer/octicons-react';
import changelogData from '../data/changelogData.js';

const Changelog = () => {
    // Map type to icon component
    const getTypeIcon = (type) => {
        switch (type) {
            case 'major': return <RocketIcon size={20} />;
            case 'feature': return <SparkleFillIcon size={20} />;
            case 'improvement': return <ToolsIcon size={20} />;
            default: return <GitCommitIcon size={20} />;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'major': return 'done';
            case 'feature': return 'accent';
            case 'improvement': return 'attention';
            default: return 'neutral';
        }
    };

    const getTypeBadge = (type) => {
        switch (type) {
            case 'major': return 'Major Release';
            case 'feature': return 'New Feature';
            case 'improvement': return 'Enhancement';
            default: return 'Update';
        }
    };

    return (
        <Box>
            {/* Changelog Timeline */}
            <Box>
                {changelogData.map((change, index) => (
                    <Box
                        key={change.version}
                        mb={4}
                        p={4}
                        bg="canvas.default"
                        border="1px solid"
                        borderColor="border.default"
                        borderRadius={1}
                        sx={{
                            '&:hover': {
                                borderColor: 'border.muted',
                                bg: 'canvas.subtle'
                            }
                        }}
                    >
                        {/* Version Header */}
                        <Box display="flex" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                            <Box 
                                display="flex" 
                                alignItems="center" 
                                color={`${getTypeColor(change.type)}.fg`}
                                mr={3}
                            >
                                {getTypeIcon(change.type)}
                            </Box>
                            
                            <Text 
                                fontSize={2} 
                                fontWeight="bold" 
                                color="fg.default"
                                mr={3}
                            >
                                v{change.version}
                            </Text>
                            
                            <Box
                                px={2}
                                py={1}
                                bg={`${getTypeColor(change.type)}.subtle`}
                                borderRadius={1}
                                mr={3}
                            >
                                <Text fontSize={0} fontWeight="medium" color={`${getTypeColor(change.type)}.fg`}>
                                    {getTypeBadge(change.type)}
                                </Text>
                            </Box>
                            
                            <Text fontSize={0} color="fg.muted">
                                {change.date}
                            </Text>
                        </Box>

                        {/* Title and Description */}
                        <Box mb={3}>
                            <Text 
                                fontSize={1} 
                                fontWeight="semibold" 
                                color="fg.default"
                                mb={2}
                                display="block"
                            >
                                {change.title}
                            </Text>
                            <Text 
                                fontSize={0} 
                                color="fg.muted"
                                lineHeight={1.5}
                                display="block"
                            >
                                {change.description}
                            </Text>
                        </Box>

                        {/* Detailed Changes */}
                        <Box>
                            <Text fontSize={0} fontWeight="medium" color="fg.default" mb={2}>
                                Key Changes:
                            </Text>
                            <Box as="ul" sx={{ pl: 0, listStyle: 'none' }}>
                                {change.details.map((detail, detailIndex) => (
                                    <Box 
                                        as="li" 
                                        key={detailIndex}
                                        display="flex"
                                        alignItems="flex-start"
                                        mb={1}
                                    >
                                        <Text color="accent.fg" mr={2}>•</Text>
                                        <Text fontSize={0} color="fg.muted" lineHeight={1.4}>
                                            {detail}
                                        </Text>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* Footer Note */}
            <Box 
                mt={4}
                p={3}
                bg="accent.subtle"
                borderRadius={1}
                border="1px solid"
                borderColor="accent.muted"
            >
                <Text fontSize={0} color="fg.muted" textAlign="center">
                    This website continues to evolve! Check back regularly for new features, algorithm additions, and improvements. 
                    <br />
                    Have suggestions? Feel free to reach out via the contact form in the footer.
                </Text>
            </Box>
        </Box>
    );
};

export default Changelog; 